import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
	Box,
	Button,
	Checkbox,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { ConfirmationModal } from "@packsize/components";
import { useToast } from "@packsize/toast";
import { DateTime } from "luxon";

import { packNetAxios } from "@/lib/api-client";
import { baseRoutes } from "@/api/base-routes";
import { EmptyState } from "@/components/empty-state";
import PackagingSolutionActionMenu from "@/views/dashboard/widgets/components/packaging-solution-action-menu";
import { useCanManageJobs } from "@/hooks/use-can-manage-jobs";

export interface FailedJobDetails {
	internalUniqueId: string;
	alias: string;
	importDateUTC: string;
	failureDateUTC: string;
	failureReason: string;
}

interface OptimalProductionFailedProps {
	readonly details: FailedJobDetails[];
	readonly count: number;
	readonly refetch: () => void;
}

type SortField = "alias" | "importDateUTC" | "failureDateUTC";
type SortDirection = "asc" | "desc";

const OptimalProductionFailed = ({ details, count, refetch }: OptimalProductionFailedProps) => {
	const intl = useIntl();
	const userCanManageJobs = useCanManageJobs();
	const { toastSuccess, toastError } = useToast();

	const [sortField, setSortField] = useState<SortField>("alias");
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
	const [selectedRows, setSelectedRows] = useState<FailedJobDetails[]>([]);
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
	const [idsToBeRemoved, setIdsToBeRemoved] = useState<string[]>([]);
	const [removeTitleSuffix, setRemoveTitleSuffix] = useState("");

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const sortedDetails = [...details].sort((a, b) => {
		const aVal = a[sortField];
		const bVal = b[sortField];
		const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
		return sortDirection === "asc" ? cmp : -cmp;
	});

	const isSelected = (row: FailedJobDetails) => selectedRows.some((r) => r.internalUniqueId === row.internalUniqueId);

	const handleRowSelect = (row: FailedJobDetails) => {
		if (isSelected(row)) {
			setSelectedRows(selectedRows.filter((r) => r.internalUniqueId !== row.internalUniqueId));
		} else {
			setSelectedRows([...selectedRows, row]);
		}
	};

	const handleSelectAll = () => {
		if (selectedRows.length === details.length) {
			setSelectedRows([]);
		} else {
			setSelectedRows([...details]);
		}
	};

	const openRemoveModal = (ids: string[], titleSuffix: string) => {
		setIdsToBeRemoved(ids);
		setRemoveTitleSuffix(titleSuffix);
		setIsRemoveModalOpen(true);
	};

	const removeItem = (internalUniqueId: string, alias: string) => {
		openRemoveModal([internalUniqueId], alias);
	};

	const removeItems = () => {
		const suffix =
			selectedRows.length === 1
				? selectedRows[0].alias
				: intl.formatMessage({ id: "{count} Jobs", defaultMessage: "{count} Jobs" }, { count: selectedRows.length });
		openRemoveModal(
			selectedRows.map((r) => r.internalUniqueId),
			suffix,
		);
	};

	const removeJobMutation = useMutation({
		mutationFn: () => packNetAxios.post(`${baseRoutes.audit}/v1/Audit/packagingSolutions/hideByIds`, idsToBeRemoved),
		onSuccess: () => {
			setSelectedRows([]);
			refetch();
			toastSuccess({
				message: intl.formatMessage({ id: "Successfully Removed Jobs", defaultMessage: "Successfully Removed Jobs" }),
			});
		},
		onError: (error) => {
			console.error("Failed to remove selected jobs", error);
			toastError({
				message: intl.formatMessage({ id: "Failed To Remove Jobs", defaultMessage: "Failed To Remove Jobs" }),
			});
		},
	});

	const handleModalResponse = (confirmed: boolean) => {
		if (confirmed) {
			removeJobMutation.mutate();
		}
		setIsRemoveModalOpen(false);
	};

	return (
		<Stack gap={2} sx={{ height: "100%", overflow: "hidden" }}>
			<Stack direction="row" justifyContent="space-between" alignItems="start">
				<Typography variant="h4">
					<FormattedMessage
						id="{count, plural, one {# Item} other {# Items}}"
						defaultMessage="{count, plural, one {# Item} other {# Items}}"
						values={{ count }}
					/>
				</Typography>
				<Button
					variant="contained"
					onClick={removeItems}
					data-pendoid="optimal-production-failed__button__remove-selected-jobs-optimal-production-failed"
					disabled={Boolean(selectedRows.length) === false}
					sx={{ visibility: selectedRows.length ? undefined : "hidden" }}
				>
					<FormattedMessage id="Remove Selected Jobs" defaultMessage="Remove Selected Jobs" />
				</Button>
			</Stack>
			<TableContainer>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							{userCanManageJobs === true && (
								<TableCell padding="checkbox">
									<Checkbox
										indeterminate={selectedRows.length > 0 && selectedRows.length < details.length}
										checked={details.length > 0 && selectedRows.length === details.length}
										onChange={handleSelectAll}
									/>
								</TableCell>
							)}
							<TableCell sortDirection={sortField === "alias" ? sortDirection : false}>
								<TableSortLabel
									active={sortField === "alias"}
									direction={sortField === "alias" ? sortDirection : "asc"}
									onClick={() => handleSort("alias")}
								>
									<FormattedMessage id="Name" defaultMessage="Name" />
								</TableSortLabel>
							</TableCell>
							<TableCell sortDirection={sortField === "importDateUTC" ? sortDirection : false}>
								<TableSortLabel
									active={sortField === "importDateUTC"}
									direction={sortField === "importDateUTC" ? sortDirection : "asc"}
									onClick={() => handleSort("importDateUTC")}
								>
									<FormattedMessage id="Imported On" defaultMessage="Imported On" />
								</TableSortLabel>
							</TableCell>
							<TableCell sortDirection={sortField === "failureDateUTC" ? sortDirection : false}>
								<TableSortLabel
									active={sortField === "failureDateUTC"}
									direction={sortField === "failureDateUTC" ? sortDirection : "asc"}
									onClick={() => handleSort("failureDateUTC")}
								>
									<FormattedMessage id="Failed On" defaultMessage="Failed On" />
								</TableSortLabel>
							</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{details.length === 0 && (
							<TableRow>
								<TableCell colSpan={1000}>
									<EmptyState>
										<FormattedMessage
											id="There are no records to display"
											defaultMessage="There are no records to display"
										/>
									</EmptyState>
								</TableCell>
							</TableRow>
						)}
						{sortedDetails.map((row) => (
							<TableRow key={row.internalUniqueId} selected={isSelected(row)} hover>
								{userCanManageJobs === true && (
									<TableCell padding="checkbox">
										<Checkbox checked={isSelected(row)} onChange={() => handleRowSelect(row)} />
									</TableCell>
								)}
								<TableCell>{row.alias}</TableCell>
								<TableCell>{DateTime.fromISO(row.importDateUTC).toLocaleString(DateTime.DATETIME_SHORT)}</TableCell>
								<TableCell>{DateTime.fromISO(row.failureDateUTC).toLocaleString(DateTime.DATETIME_SHORT)}</TableCell>
								<TableCell sx={{ maxWidth: 48 }}>
									<PackagingSolutionActionMenu
										internalUniqueId={row.internalUniqueId}
										alias={row.alias}
										removeItem={removeItem}
										userCanRemove={userCanManageJobs}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<ConfirmationModal
				title={intl.formatMessage(
					{ id: "Remove {aliasToBeRemoved}", defaultMessage: "Remove {aliasToBeRemoved}" },
					{ aliasToBeRemoved: removeTitleSuffix },
				)}
				confirmText={intl.formatMessage({ id: "Remove", defaultMessage: "Remove" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				open={isRemoveModalOpen}
				onClose={() => handleModalResponse(false)}
				onCancel={() => handleModalResponse(false)}
				onConfirm={() => handleModalResponse(true)}
				size="sm"
			>
				<Box>
					{intl.formatMessage({
						id: "This change can not be undone.",
						defaultMessage: "This change can not be undone.",
					})}
				</Box>
			</ConfirmationModal>
		</Stack>
	);
};

export default OptimalProductionFailed;
